import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

interface CaseSummary {
  id: string;
  business_name: string;
  law_firm: string | null;
  event_type: string | null;
  assigned_queue: string;
  queue_reason: string;
  pipeline_stage: string;
  estimated_value: number;
  case_strength: number;
  transactions_need_review: number;
  completion_percentage: number;
  days_stale: number;
  last_updated: string | null;
  expected_ready_date: string | null;
  primary_action: string;
  primary_action_url: string;
}

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  data_specialist: number;
  accountant: number;
  ai_processing: number;
  ready: number;
  client: number;
  attorney: number;
  total_value: number;
}

interface QueueResponse {
  cases: CaseSummary[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Status badge helper
const getStatusBadge = (caseItem: CaseSummary) => {
  const { assigned_queue, days_stale, transactions_need_review, pipeline_stage } = caseItem;
  
  if (days_stale >= 7) {
    return { label: 'stale', color: 'bg-red-100 text-red-700' };
  }
  if (assigned_queue === 'ready') {
    return { label: 'ready', color: 'bg-green-100 text-green-700' };
  }
  if (assigned_queue === 'ai_processing' || pipeline_stage === 'processing') {
    return { label: 'processing', color: 'bg-blue-100 text-blue-700' };
  }
  if (assigned_queue === 'client' || pipeline_stage === 'waiting_client') {
    return { label: 'waiting', color: 'bg-slate-100 text-slate-600' };
  }
  if (transactions_need_review > 0) {
    return { label: 'review', color: 'bg-yellow-100 text-yellow-700' };
  }
  return { label: 'review', color: 'bg-yellow-100 text-yellow-700' };
};

// Action text helper
const getActionText = (caseItem: CaseSummary) => {
  const { transactions_need_review, queue_reason, assigned_queue } = caseItem;
  
  if (transactions_need_review > 0) {
    return `${transactions_need_review} transaction${transactions_need_review > 1 ? 's' : ''} need${transactions_need_review === 1 ? 's' : ''} review`;
  }
  if (assigned_queue === 'ai_processing') {
    return 'AI processing';
  }
  if (assigned_queue === 'client') {
    return 'Client follow-up needed';
  }
  if (assigned_queue === 'ready') {
    return 'Ready for accountant';
  }
  return queue_reason || 'Data entry in progress';
};

const DataSpecialistQueue: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsData = await api.get<QueueStats>('/api/v1/queue/stats?user_role=data_specialist');
      setStats(statsData);

      // Fetch queue
      const queueData = await api.get<QueueResponse>(`/api/v1/queue/data-specialist?page=${page}&page_size=20`);
      setCases(queueData.cases);
      setTotalCount(queueData.total_count);
      setTotalPages(queueData.total_pages);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleRowClick = (caseItem: CaseSummary) => {
    navigate(caseItem.primary_action_url);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Data Specialist Queue</h1>
            <p className="text-slate-600 mt-1">Review transactions and categorize data</p>
          </div>
          <Link
            to="/dashboard"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Summary Cards - Compact */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
              <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                Needs Your Attention
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900">
              {stats?.data_specialist || 0}
            </div>
            <p className="text-slate-500 text-sm mt-1">cases need transaction review</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                No Action Needed
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900">
              {stats?.no_action_needed || 0}
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {stats?.ai_processing || 0} processing • {stats?.accountant || 0} with accountants
            </p>
          </div>
        </div>

        {/* Queue Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Your Queue ({totalCount} cases)
          </h2>
          <button
            onClick={fetchData}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Refresh
          </button>
        </div>

        {/* Compact Table View */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {cases.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No cases currently need your attention. Great job! 🎉
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div className="col-span-3">Case</div>
                <div className="col-span-3">Firm</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-3">Action</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {cases.map((caseItem) => {
                  const status = getStatusBadge(caseItem);
                  const action = getActionText(caseItem);
                  
                  return (
                    <div
                      key={caseItem.id}
                      onClick={() => handleRowClick(caseItem)}
                      className="grid grid-cols-12 gap-4 px-5 py-4 hover:bg-blue-50 cursor-pointer transition-colors items-center"
                    >
                      {/* Case Name */}
                      <div className="col-span-3">
                        <span className="font-medium text-slate-900">
                          {caseItem.business_name || 'Unknown'}
                        </span>
                      </div>

                      {/* Law Firm */}
                      <div className="col-span-3">
                        <span className="text-slate-600">
                          {caseItem.law_firm || 'No law firm'}
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className="col-span-1 text-center">
                        <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>

                      {/* Action */}
                      <div className="col-span-3">
                        <span className="text-slate-600 text-sm">
                          {action}
                        </span>
                      </div>

                      {/* Amount */}
                      <div className="col-span-2 text-right">
                        <span className="font-medium text-slate-900">
                          {caseItem.estimated_value > 0 
                            ? formatCurrency(caseItem.estimated_value)
                            : '—'
                          }
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSpecialistQueue;
